import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CloseOutlined, MenuOutlined } from "@ant-design/icons";
import { useUnit } from "effector-react";
import BasicLink from "@components/core/links/BasicLink";
import HeaderOfficeDropdown from "@components/core/headers/HeaderOfficeDropdown";
import HeaderUserDropdown from "@components/core/headers/HeaderUserDropdown";
import SideMenu from "@components/core/menus/SideMenu";
import BasicButton from "@components/core/buttons/BasicButton";
import { ROUTES } from "@routes/Routes";
import { authenticationStore } from "@state/auth/AuthStore";
import LogoutIcon from "@static/core/icons/icon-logout.svg?react";
import HomeIcon from "@static/core/icons/icon-home.svg?react";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import { useNavigate } from "react-router-dom";
import { StatusEnum } from "@utils/enums/status.enum";
import { clearUser } from "@/state/auth/AuthEvents";

interface HeaderProps {
  noMenu: boolean;
  hideDropdown?: boolean;
  className?: string;
}

const Header: FunctionComponent<HeaderProps> = ({
  className = "",
  noMenu,
  hideDropdown = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authenticationContext = useUnit<AuthenticationContext | null>(
    authenticationStore,
  );
  const user = authenticationContext?.user;
  const certificateStatus = authenticationContext?.user?.tsCertificateStatus;
  const [dropdownOpen, setDropdownOpen] = useState<string>();
  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  useEffect(() => {
    if (user && !isLoggingOut) {
      if (
        user.onboarding &&
        location.pathname !== ROUTES.extranet.onboarding.generate() &&
        location.pathname !== "/extranet/onboarding/processing" &&
        location.pathname !== "/extranet/required/auth" &&
        !(
          location.pathname.startsWith("/extranet/editor") &&
          certificateStatus &&
          certificateStatus === StatusEnum.VALID
        )
      ) {
        navigate(ROUTES.extranet.onboarding.generate());
      }
    }
  }, [user, certificateStatus, navigate]);

  // Once the clearUser is done, we navigate to logout
  useEffect(() => {
    if (authenticationContext === null && isLoggingOut) {
      // Trigger navigation when authenticationStore is cleared
      navigate(ROUTES.auth.logout.generate());
      setIsLoggingOut(false);
    }
  }, [authenticationContext, navigate]);

  const changeDropdownOpen = (dropdown: string) => {
    if (dropdownOpen === dropdown) {
      setDropdownOpen(undefined);
    } else {
      setDropdownOpen(dropdown);
    }
  };

  const handleLogout = () => {
    setIsLoggingOut(true);
    clearUser();
  };

  return (
    <>
      <header className={className}>
        <div className="header-container content-contained content-xl">
          <div className="logo-container">
            <BasicLink to="https://ecma-solutions.com/produits-expert-comptable/jesignexpert">
              <img
                alt={t("header.logo.alt")}
                className="logo"
                src="/img/logo.svg"
              />
            </BasicLink>
          </div>
          {!noMenu && authenticationContext && authenticationContext.user ? (
            <div className="content-container">
              <div className="home">
                <BasicLink
                  to={ROUTES.extranet.home.generate()}
                  className="home-link"
                  dataAlt="Accueil"
                >
                  <HomeIcon className="icon-home" />
                </BasicLink>
              </div>
              <div
                className={`app-dropdown-header-overlay ${
                  dropdownOpen ? "" : "hidden"
                }`}
                onClick={() => setDropdownOpen(undefined)}
              />
              <HeaderOfficeDropdown
                show={dropdownOpen === "office"}
                setShow={() => changeDropdownOpen("office")}
                hideDropdown={hideDropdown}
              />
              <HeaderUserDropdown
                show={dropdownOpen === "user"}
                setShow={() => changeDropdownOpen("user")}
                hideDropdown={hideDropdown}
              />
              <div className="user">
                <BasicLink
                  to={ROUTES.auth.logout.generate()}
                  onClick={() => handleLogout()}
                  className="user-logout"
                >
                  <LogoutIcon className="icon-logout" />
                </BasicLink>
              </div>
              <div className="menu">
                <BasicButton
                  variant="side-menu"
                  icon={isSideMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
                  onClick={() => {
                    if (!isSideMenuOpen && dropdownOpen !== "user") {
                      changeDropdownOpen("user");
                    } else if (isSideMenuOpen) {
                      changeDropdownOpen("");
                    }
                    setIsSideMenuOpen(!isSideMenuOpen);
                  }}
                />
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </header>
      <SideMenu
        isSideMenuOpen={isSideMenuOpen}
        dropdownOpen={dropdownOpen}
        changeDropdownOpen={changeDropdownOpen}
      />
    </>
  );
};

export default Header;
